import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { chapters, type ServiceInfoChapter } from './chapters';

export const SERVICE_INFO_QUERY_PARAM = 'info';
export const SERVICE_INFO_CHAPTER_QUERY_PARAM = 'infoChapter';

export const isServiceInfoChapter = (chapter: string | null): chapter is ServiceInfoChapter =>
    Object.values(chapters).includes(chapter as ServiceInfoChapter);

export const hasServiceInfoQueryParam = (searchParams: URLSearchParams) => searchParams.has(SERVICE_INFO_QUERY_PARAM);

export const shouldShowServiceInfo = (searchParams: URLSearchParams) =>
    searchParams.get(SERVICE_INFO_QUERY_PARAM) === 'true';

export const getServiceInfoChapterFromSearchParams = (searchParams: URLSearchParams) => {
    const chapter = searchParams.get(SERVICE_INFO_CHAPTER_QUERY_PARAM);

    return isServiceInfoChapter(chapter) ? chapter : undefined;
};

export const shouldShowServiceInfoPopover = (searchParams: URLSearchParams) =>
    shouldShowServiceInfo(searchParams) && !getServiceInfoChapterFromSearchParams(searchParams);

export const hasServiceInfoSearchParamsState = (
    searchParams: URLSearchParams,
    serviceInfoChapter?: ServiceInfoChapter
) =>
    shouldShowServiceInfo(searchParams) &&
    (serviceInfoChapter
        ? searchParams.get(SERVICE_INFO_CHAPTER_QUERY_PARAM) === serviceInfoChapter
        : !searchParams.has(SERVICE_INFO_CHAPTER_QUERY_PARAM));

export const getServiceInfoSearchParams = (searchParams: URLSearchParams, serviceInfoChapter?: ServiceInfoChapter) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.set(SERVICE_INFO_QUERY_PARAM, 'true');

    if (serviceInfoChapter) {
        nextSearchParams.set(SERVICE_INFO_CHAPTER_QUERY_PARAM, serviceInfoChapter);
    } else {
        nextSearchParams.delete(SERVICE_INFO_CHAPTER_QUERY_PARAM);
    }

    return nextSearchParams;
};

export const getSearchParamsWithoutServiceInfo = (searchParams: URLSearchParams) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete(SERVICE_INFO_QUERY_PARAM);
    nextSearchParams.delete(SERVICE_INFO_CHAPTER_QUERY_PARAM);

    return nextSearchParams;
};

export const useServiceInfoSearchParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const showServiceInfo = useCallback(
        (serviceInfoChapter?: ServiceInfoChapter) => {
            setSearchParams(getServiceInfoSearchParams(searchParams, serviceInfoChapter), { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const hideServiceInfo = useCallback(() => {
        setSearchParams(getSearchParamsWithoutServiceInfo(searchParams), { replace: true });
    }, [searchParams, setSearchParams]);

    return { searchParams, showServiceInfo, hideServiceInfo };
};
